import { useState } from "react";
import { Button, ButtonVariant, CheckboxCard } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import { IncomeType } from "shared/types";
import { CombinedIncomeLayout } from "../components/Layout";
import { CombinedIncomeSubStep } from "../../../constants";
import { toast } from "react-toastify";
import { useCombinedIncome } from "../context";
import { CombinedIncomeMode } from "../types";
import { getErrorMessage } from "utils/error";
import {
  COMBINED_INCOME_FORM_ID,
  incomeTypeOptions,
  newIncomeSourceOptions
} from "../constants";

const DEFAULT_STEP_DATA = {
  currentIncomeId: null,
  company_name: "",
  job_title: "",
  start_date: "",
  supervisor: {
    first_name: "",
    last_name: "",
    contact_email: "",
    contact_phone: ""
  },
  description: "",
  paid_from: "",
  pay_period: "",
  amount: ""
};

export const SelectIncomeTypeStep = () => {
  const {
    updateStepMutation,
    getNextStep,
    stepData,
    refetchVerification,
    mode
  } = useCombinedIncome();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedIncomeType, setSelectedIncomeType] =
    useState<IncomeType | null>(stepData.currentIncomeType ?? null);

  const handleSelectIncomeType = async (incomeType: IncomeType) => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const nextStep = getNextStep(CombinedIncomeSubStep.SelectIncomeType, {
        incomeType
      });

      if (!nextStep) return;

      const resetStepData =
        incomeType === stepData.currentIncomeType ? {} : DEFAULT_STEP_DATA;

      updateStepMutation.mutate(
        {
          lastStep: nextStep,
          currentIncomeType: incomeType,
          ...resetStepData
        },
        {
          onSuccess: () => {
            refetchVerification();
          }
        }
      );
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to process income type");
    } finally {
      setIsProcessing(false);
    }
  };

  const isManualApplicationFlow = mode === CombinedIncomeMode.ManualApplication;
  const options = isManualApplicationFlow
    ? incomeTypeOptions
    : newIncomeSourceOptions;

  const onChange = (incomeType: IncomeType) => {
    if (isManualApplicationFlow) handleSelectIncomeType(incomeType);
    else setSelectedIncomeType(incomeType);
  };

  return (
    <form
      id={COMBINED_INCOME_FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
        if (selectedIncomeType) void handleSelectIncomeType(selectedIncomeType);
      }}
    >
      <CombinedIncomeLayout
        title="What is applicant's current income?"
        footer={
          isManualApplicationFlow ? null : (
            <Button
              type="submit"
              form={COMBINED_INCOME_FORM_ID}
              variant={ButtonVariant.PRIMARY}
              className="w-[125px]"
              loading={isProcessing}
              disabled={selectedIncomeType === null}
            >
              Continue
            </Button>
          )
        }
      >
        <div className="space-y-3">
          {options.map((option) => (
            <CheckboxCard
              key={option.type}
              checked={
                !isManualApplicationFlow && option.type === selectedIncomeType
              }
              value={option.type}
              id={option.label}
              label={
                <div className="flex items-center gap-4">
                  <div className="flex justify-center items-center bg-primary-20 w-10 h-10 rounded-lg">
                    <HugeiconsIcon
                      icon={option.icon}
                      size={24}
                      className="text-primary-500"
                    />
                  </div>
                  <p className="text-base text-alpha-900 font-bold">
                    {option.label}
                  </p>
                </div>
              }
              name={option.label}
              wrapperClassName="w-full"
              onChange={() => onChange(option.type)}
            />
          ))}
        </div>
      </CombinedIncomeLayout>
    </form>
  );
};
